import { createContext, useContext, useState, useCallback, useRef, useMemo } from "react";
import type { ReactNode, RefObject } from "react";
import type { MarkerDatum } from "@/hooks/useMapData";

interface SearchContextValue {
  /** Ref to current marker data — read in event handlers / memos, not for rendering. */
  markerDataRef: RefObject<MarkerDatum[]>;
  /** Ref to current map bounds — read in event handlers / memos, not for rendering. */
  mapBoundsRef: RefObject<google.maps.LatLngBounds | null>;
  /** Callback registered by MapPage; null when not on the map page. */
  onSearchSelectRef: RefObject<((id: string) => void) | null>;
  /** Whether the map page is currently mounted. */
  isOnMapPage: boolean;
  setMarkerData: (data: MarkerDatum[]) => void;
  setMapBounds: (bounds: google.maps.LatLngBounds | null) => void;
  registerMapHandler: (fn: (id: string) => void) => void;
  clearMapHandler: () => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

export function SearchProvider({ children }: { children: ReactNode }) {
  const markerDataRef = useRef<MarkerDatum[]>([]);
  const mapBoundsRef = useRef<google.maps.LatLngBounds | null>(null);
  const onSearchSelectRef = useRef<((id: string) => void) | null>(null);

  // Only boolean state — minimises re-renders of consumers.
  const [isOnMapPage, setIsOnMapPage] = useState(false);

  const setMarkerData = useCallback((data: MarkerDatum[]) => {
    markerDataRef.current = data;
  }, []);

  const setMapBounds = useCallback((bounds: google.maps.LatLngBounds | null) => {
    mapBoundsRef.current = bounds;
  }, []);

  const registerMapHandler = useCallback((fn: (id: string) => void) => {
    onSearchSelectRef.current = fn;
    setIsOnMapPage(true);
  }, []);

  const clearMapHandler = useCallback(() => {
    onSearchSelectRef.current = null;
    markerDataRef.current = [];
    mapBoundsRef.current = null;
    setIsOnMapPage(false);
  }, []);

  // Stable context value — only changes when isOnMapPage toggles.
  const value = useMemo<SearchContextValue>(() => ({
    markerDataRef,
    mapBoundsRef,
    onSearchSelectRef,
    isOnMapPage,
    setMarkerData,
    setMapBounds,
    registerMapHandler,
    clearMapHandler,
  }), [isOnMapPage, setMarkerData, setMapBounds, registerMapHandler, clearMapHandler]);

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
}

export function useSearchContext() {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearchContext must be used within SearchProvider");
  return ctx;
}
